import { ModeDot } from '@/components/ModeSwitcher';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface AllowedEnvironmentsValue {
  production: boolean;
  sandbox: boolean;
}

interface AllowedEnvironmentsFieldProps {
  value: AllowedEnvironmentsValue;
  onChange: (next: AllowedEnvironmentsValue) => void;
  /** Disable the entire fieldset (e.g. lacking SuperAdmin). */
  disabled?: boolean;
  /** Surface-specific helper text. */
  description?: string;
  /** Show a "at least one must be checked" error below the field. */
  error?: string;
}

/**
 * Two-checkbox group for granting a user access to Production / Sandbox.
 * Used in both admin and portal user-management dialogs. Returns the
 * current selection as a `{ production, sandbox }` boolean pair; the
 * caller maps that to whatever wire shape Phase B's OpenAPI generates
 * (likely `AllowedEnvironments: ['Production', 'Sandbox']`).
 *
 * The form-level validator (zod / RHF) enforces "at least one checked".
 */
export function AllowedEnvironmentsField({
  value,
  onChange,
  disabled,
  description,
  error,
}: AllowedEnvironmentsFieldProps) {
  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <Label>Allowed environments</Label>
      {description ? (
        <p className="text-xs text-muted-foreground">{description}</p>
      ) : null}
      <div className="grid grid-cols-2 gap-2">
        <CheckboxRow
          label="Production"
          mode="production"
          checked={value.production}
          onChange={(next) => onChange({ ...value, production: next })}
          disabled={disabled}
        />
        <CheckboxRow
          label="Sandbox"
          mode="sandbox"
          checked={value.sandbox}
          onChange={(next) => onChange({ ...value, sandbox: next })}
          disabled={disabled}
        />
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </fieldset>
  );
}

interface CheckboxRowProps {
  label: string;
  mode: 'production' | 'sandbox';
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}

function CheckboxRow({ label, mode, checked, onChange, disabled }: CheckboxRowProps) {
  return (
    <label
      data-testid={`allowed-env-${mode}`}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
        checked
          ? 'border-primary/60 bg-primary/5 text-foreground'
          : 'border-input bg-background text-muted-foreground hover:bg-accent/40',
        disabled ? 'cursor-not-allowed opacity-60' : null,
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5"
      />
      <ModeDot mode={mode} />
      <span>{label}</span>
    </label>
  );
}

/** Convenience: derive the bool pair from an `AllowedEnvironments` string. */
export function fromAllowedEnvironments(
  allowed: 'None' | 'Sandbox' | 'Production' | 'Both' | undefined,
): AllowedEnvironmentsValue {
  if (allowed === 'Both') return { production: true, sandbox: true };
  if (allowed === 'Sandbox') return { production: false, sandbox: true };
  if (allowed === 'None') return { production: false, sandbox: false };
  // `Production` and missing/undefined both default to production-only.
  return { production: true, sandbox: false };
}

/** Serialise the bool pair back to the wire-shape array Phase B expects. */
export function toAllowedEnvironmentsArray(
  value: AllowedEnvironmentsValue,
): Array<'Production' | 'Sandbox'> {
  const out: Array<'Production' | 'Sandbox'> = [];
  if (value.production) out.push('Production');
  if (value.sandbox) out.push('Sandbox');
  return out;
}
